import React, { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import ReactSelect from "react-select";
import { InputError } from "../../../commons/MicroComponents";
import { CheckBox, InputWithLabel } from "../../../components/micro";
import FileUploadChoose from "../../../commons/FileUploadChoose";
import {
  addDirectorShareholder,
  emailExist,
  existingUsers,
} from "../../../redux/companies/slice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getOrganisationDetails } from "../../../redux/organisation/slice";
import { uploadFile } from "../../../redux/uploadFile/slice";
import debounce from "lodash/debounce";

const AddDirector = ({
  setShowDirectorModel,
  company_id,
  setDirectorsArray,
  directorsArray,
  isEdit,
}) => {
  const [existingUsersArray, setExistingUsersArray] = useState([]);
  const [isLoad, setIsLoad] = useState(false);
  const [isFullNameDisabled, setIsFullNameDisabled] = useState(false);
  const [isCitizenshipDisabled, setIsCitizenshipDisabled] = useState(false);
  const [identityDocuments, setIdentityDocuments] = useState([]);
  const debouncedEmailCheckRef = useRef();
  const dispatch = useDispatch();

  const citizenshipOptions = [
    { label: "Singaporean", value: "singaporean" },
    {
      label: "Permanent Resident",
      value: "permanent_resident",
    },
    { label: "Foreigners", value: "foreigners" },
  ];

  // Selector
  const { organisationDetails, fileUploadLoading } = useSelector((store) => ({
    organisationDetails:
      store?.organisationData?.getOrganisationDetailsStatus?.data?.data,
    fileUploadLoading: store?.uploadFileData?.loading,
  }));

  useEffect(() => {
    dispatch(existingUsers());
  }, [dispatch]);

  useEffect(() => {
    if (organisationDetails) {
      let data = organisationDetails?.nominee_directors?.map((x) => ({
        ...x,
        label: x.full_name,
      }));
      setExistingUsersArray(data);
    }
  }, [organisationDetails]);

  useEffect(() => {
    debouncedEmailCheckRef.current = debounce(
      async (email, setFieldValue, dispatch) => {
        if (!email) return;

        try {
          const res = await dispatch(emailExist({ email }));
          const userData = res?.payload?.data;
          if (userData?.valid) {
            setIsFullNameDisabled(!!userData?.data?.full_name);
            setIsCitizenshipDisabled(!!userData?.data?.citizenship);
            setFieldValue("full_name", userData?.data?.full_name || "");
            setFieldValue("email", userData?.data?.email || "");
            setFieldValue("existing_user_id", userData?.data?.id || "");
            setFieldValue("citizenship", userData?.data?.citizenship || "");
          }
        } catch (err) {
          console.error("Email check failed", err);
        }
      },
      500
    );
  }, [dispatch]);

  useEffect(() => {
    if (isEdit?.identity_documents?.length > 0) {
      const documents = isEdit?.identity_documents.map((doc) => ({
        id: doc.company_document.document.id,
        name: doc.company_document.document.name,
      }));
      setIdentityDocuments(documents);
    }
  }, [isEdit]);

  // Handle file upload for identity documents
  const handleIdentityUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const type = file?.type;
      const formData = new FormData();
      formData.append("type", "director-identity");
      formData.append("file", file);
      formData.append(
        "document_type",
        type === "application/pdf" ? "doc" : "img"
      );
      dispatch(uploadFile(formData)).then((res) => {
        if (res.type === "uploadFile/fulfilled") {
          setIdentityDocuments((prevArray) => [...prevArray, res.payload.data]);
        }
      });
    });
  };

  // Remove identity document
  const removeIdentityDocument = (id) => {
    setIdentityDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const shareholderTypeOptions = [
    { label: "Individual Shareholder", value: "individual" },
    { label: "Corporate Shareholder", value: "corporate" },
  ];

  return (
    <>
      <div
        className="model"
        onClick={() => {
          setShowDirectorModel(false);
        }}
      >
        <div
          className="bg-white flex flex-col items-center justify-center drop-shadow-lg rounded-lg w-full max-w-lg min-h-40 py-6 px-6 sm:mx-auto relative mx-6"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <h2 className="sm:text-2xl text-base font-ebGaramond border-b border-secondary_grey w-full pb-3 mb-4">
            {isEdit.id ? "Edit Director" : "Add Director"}
          </h2>
          <Formik
            enableReinitialize={true}
            initialValues={{
              existing_user_id: isEdit?.existing_user_id || isEdit?.user?.id,
              existing_users: isEdit?.user?.existing_users ?? "",
              existing_contact: "",
              full_name: isEdit?.user?.full_name ?? "",
              email: isEdit?.user?.email ?? "",
              main_signee: isEdit?.main_signee ?? false,
              citizenship: isEdit?.citizenship ?? "",
              is_shareholder: isEdit?.is_shareholder ?? false,
              shareholder_type: isEdit?.shareholder_type ?? "",
              passport_fin_number: isEdit?.passport_fin_number ?? "",
              type: "director",
            }}
            validationSchema={Yup.object().shape({
              existing_contact: Yup.object(),
              full_name: Yup.string().required("Please Enter Full Name"),
              email: Yup.string().required("Please Enter Email"),
              citizenship: Yup.string().required("Please Select Citizenship"),
              main_signee: Yup.boolean().required("Please Select Main Signee"),
              passport_fin_number: Yup.string().required(
                "Please Enter Passport/NRIC/FIN Number"
              ),
              shareholder_type: Yup.string().when("is_shareholder", {
                is: true,
                then: Yup.string().required("Please Select Shareholder Type"),
                otherwise: Yup.string(),
              }),
            })}
            onSubmit={(values) => {
              const payload = {
                ...(isEdit && { id: isEdit.id }),
                existing_contact: values?.existing_contact?.id,
                full_name: values?.full_name,
                email: values?.email,
                main_signee: values?.main_signee,
                citizenship: values?.citizenship,
                is_shareholder: values?.is_shareholder,
                ...(values.is_shareholder && {
                  shareholder_type: values?.shareholder_type,
                }),
                ...(values?.passport_fin_number && {
                  passport_fin_number: values?.passport_fin_number,
                }),
                ...(identityDocuments?.length > 0 && {
                  identity_document_ids: identityDocuments.map((doc) => doc.id),
                }),
                company_id: company_id,
                ...(values.existing_user_id && {
                  existing_user_id: values.existing_user_id,
                }),
                type: "resident_director",
              };
              setIsLoad(true);
              dispatch(addDirectorShareholder(payload)).then((res) => {
                setIsLoad(false);
                if (res.type === "addDirectorShareholder/fulfilled") {
                  if (isEdit) {
                    // Update existing director in the array
                    const updatedArray = directorsArray.map((director) =>
                      director.id === isEdit.id ? res.payload.data : director
                    );
                    setDirectorsArray(updatedArray);
                  } else {
                    // Add new director to the array
                    setDirectorsArray([...directorsArray, res.payload.data]);
                  }
                  toast.success(res.payload.message);
                  setShowDirectorModel(false);
                  dispatch(getOrganisationDetails(company_id));
                }
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => {
              const handleEmailChange = (e) => {
                const email = e.target.value;
                setFieldValue("email", email);
                if (debouncedEmailCheckRef.current) {
                  debouncedEmailCheckRef.current(
                    email,
                    setFieldValue,
                    dispatch
                  );
                }
              };
              return (
                <Form
                  className="w-full space-y-3 max-h-[500px] overflow-y-auto"
                  onSubmit={handleSubmit}
                >
                  <InputWithLabel
                    label={"Email"}
                    type={"email"}
                    className={
                      errors.email && touched.email
                        ? ` input-error `
                        : ` border `
                    }
                    idFromName="email"
                    onChange={handleEmailChange}
                    onBlur={handleBlur}
                    value={values.email}
                    errors={errors.email && touched.email}
                    errorsText={errors.email}
                  />
                  {/* Existing Contact */}
                  {!!organisationDetails?.length && (
                    <div>
                      <ReactSelect
                        className={"text-black sm:text-base text-sm p-0"}
                        id="existing_contact"
                        placeholder="Existing Contact"
                        options={existingUsersArray}
                        isLoading={false}
                        getOptionValue={(existing_contact) =>
                          existing_contact.value
                        }
                        value={values.existing_contact}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setFieldValue("existing_contact", e);
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: `1px solid ${
                              errors.existing_contact &&
                              touched.existing_contact
                                ? "red"
                                : "#0000003B"
                            }`,
                            borderRadius: "0.25rem",
                            padding: "6px 4px",
                            boxShadow: "none",
                            "&:hover": {
                              border: "1px solid #0000003B",
                            },
                          }),
                        }}
                      />
                      {errors.existing_contact && touched.existing_contact ? (
                        <InputError errorTitle={errors.existing_contact} />
                      ) : null}
                    </div>
                  )}
                  <InputWithLabel
                    label={"Full Name"}
                    type={"text"}
                    className={
                      errors.full_name && touched.full_name
                        ? ` input-error `
                        : ` border `
                    }
                    idFromName="full_name"
                    onChange={handleChange}
                    disabled={isFullNameDisabled}
                    onBlur={handleBlur}
                    value={values.full_name}
                    errors={errors.full_name && touched.full_name}
                    errorsText={errors.full_name}
                  />
                  {/* Citizenship */}
                  <div>
                    <ReactSelect
                      className={"text-black sm:text-base text-sm p-0"}
                      id="citizenship"
                      placeholder="Citizenship"
                      options={citizenshipOptions}
                      isLoading={false}
                      isDisabled={isCitizenshipDisabled}
                      getOptionValue={(options) => options.value}
                      getOptionLabel={(options) => options.label}
                      value={citizenshipOptions.find(
                        (op) => op.value === values.citizenship
                      )}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        setFieldValue("citizenship", e.value);
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          border: `1px solid ${
                            errors.citizenship && touched.citizenship
                              ? "red"
                              : "#0000003B"
                          }`,
                          borderRadius: "0.25rem",
                          padding: "6px 4px",
                          boxShadow: "none",
                          "&:hover": {
                            border: "1px solid #0000003B",
                          },
                        }),
                      }}
                    />
                    {errors.citizenship && touched.citizenship ? (
                      <InputError errorTitle={errors.citizenship} />
                    ) : null}
                  </div>

                  {/* Passport/NRIC/FIN Number */}
                  <InputWithLabel
                    label={"Passport/NRIC/FIN Number"}
                    type={"text"}
                    className={
                      errors.passport_fin_number && touched.passport_fin_number
                        ? ` input-error `
                        : ` border `
                    }
                    idFromName="passport_fin_number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.passport_fin_number}
                    errors={
                      errors.passport_fin_number && touched.passport_fin_number
                    }
                    errorsText={errors.passport_fin_number}
                  />

                  <div>
                    <CheckBox
                      idFrom={"main_signee"}
                      label={"Main Signee"}
                      name={"main_signee"}
                      value={values.main_signee}
                      checked={values.main_signee}
                      onChange={(e) => {
                        setFieldValue("main_signee", e.target.checked);
                      }}
                      className={""}
                    />
                  </div>
                  <div>
                    <CheckBox
                      idFrom={"is_shareholder"}
                      label={"Is Shareholder"}
                      name={"is_shareholder"}
                      value={values.is_shareholder}
                      checked={values.is_shareholder}
                      onChange={(e) => {
                        handleChange(e);
                        if (!e.target.checked) {
                          setFieldValue("shareholder_type", "");
                        }
                      }}
                      className={""}
                    />
                  </div>
                  {/* Shareholder Type Dropdown - Only show when is_shareholder is checked */}
                  {values.is_shareholder && (
                    <div>
                      <ReactSelect
                        className={"text-black sm:text-base text-sm p-0"}
                        id="shareholder_type"
                        placeholder="Shareholder Type"
                        options={shareholderTypeOptions}
                        isLoading={false}
                        getOptionValue={(options) => options.value}
                        getOptionLabel={(options) => options.label}
                        value={shareholderTypeOptions.find(
                          (op) => op.value === values.shareholder_type
                        )}
                        onBlur={handleBlur}
                        onChange={(e) => {
                          setFieldValue("shareholder_type", e.value);
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: `1px solid ${
                              errors.shareholder_type &&
                              touched.shareholder_type
                                ? "red"
                                : "#0000003B"
                            }`,
                            borderRadius: "0.25rem",
                            padding: "6px 4px",
                            boxShadow: "none",
                            "&:hover": {
                              border: "1px solid #0000003B",
                            },
                          }),
                        }}
                      />
                      {errors.shareholder_type && touched.shareholder_type ? (
                        <InputError errorTitle={errors.shareholder_type} />
                      ) : null}
                    </div>
                  )}

                  {/* Identity Documents */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                      Identity Documents
                    </h3>
                    <p className="text-sm text-gray-600">
                      Upload scanned copies of NRIC/Passport/FIN documents
                    </p>
                    {identityDocuments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {identityDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded"
                          >
                            <span className="text-sm text-gray-700">
                              {doc.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeIdentityDocument(doc.id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <FileUploadChoose
                      handleFileChange={handleIdentityUpload}
                      className="w-full"
                    />
                  </div>

                  <div className="flex justify-end gap-4 w-full">
                    <button
                      type="button"
                      className="btn-outline uppercase"
                      onClick={() => {
                        setShowDirectorModel(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoad || fileUploadLoading}
                      className={`${
                        isLoad || fileUploadLoading
                          ? "btn-outline-disabled"
                          : "btn-theme"
                      } uppercase`}
                    >
                      {isLoad || fileUploadLoading
                        ? "Loading"
                        : isEdit.id
                        ? "Edit"
                        : "Add"}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default AddDirector;
